import { useEffect, useState } from "react";

import { api } from "@/api/client";
import { useStore } from "@/state/store";
import { Icon } from "@/components/atoms/Icons";
import { fmtRelative, fmtUSD, parseDecimal } from "@/components/atoms/format";
import type { components } from "@/api/schema.gen";

type TradeView = components["schemas"]["TradeView"];

export function ActivityView() {
  const userId = useStore((s) => s.userId);
  const [trades, setTrades] = useState<TradeView[]>([]);

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    const load = async () => {
      const { data } = await api.GET("/v1/me/trades", {
        params: { query: { limit: 50 } },
      });
      if (cancelled) return;
      if (data) setTrades(data.items);
    };
    load();
    const t = setInterval(load, 8_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [userId]);

  if (!userId) {
    return (
      <div className="card mx-6 my-6 flex flex-col items-center gap-3 py-16">
        <Icon.Activity size={28} className="text-muted" />
        <p className="text-[13px] text-muted">
          Connect a wallet to see your activity feed.
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 px-4 pb-28 pt-4 sm:px-6 lg:pb-20">
      <h2 className="text-[18px] font-semibold">Recent trades</h2>
      {trades.length === 0 ? (
        <div className="card flex items-center justify-center py-10 text-[13px] text-muted">
          No trades yet.
        </div>
      ) : (
        <div className="card p-0">
          {trades.map((t, i) => (
            <div
              key={t.id}
              className="grid items-center gap-3 border-b border-line px-4 py-3 text-[12.5px] card-in"
              style={{
                gridTemplateColumns: "auto 110px 110px 1fr auto",
                animationDelay: `${i * 25}ms`,
              }}
            >
              <span
                className="font-mono uppercase"
                style={{
                  color:
                    t.taker_side === "buy" ? "var(--yes)" : "var(--no)",
                }}
              >
                {t.taker_side === "buy" ? "▲ BUY" : "▼ SELL"}
              </span>
              <span className="font-mono">
                {parseDecimal(t.size).toFixed(2)} sh
              </span>
              <span className="font-mono">
                @ {(parseDecimal(t.price) * 100).toFixed(1)}¢
              </span>
              <span className="truncate text-muted">
                Market {t.market_id.slice(0, 8)}…
              </span>
              <span className="text-muted">{fmtRelative(t.created_at)}</span>
              <span className="font-mono text-text-2 hidden md:inline">
                {fmtUSD(parseDecimal(t.price) * parseDecimal(t.size), {
                  compact: false,
                })}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
